// In-memory business partners for demo mode (no Supabase configured). Mirrors
// the rules the UI depends on: unique immutable codes, one role at least, one
// default address per kind, keyset paging on code.

import type {
  AddressInput,
  PartnerAddress,
  PartnerDetail,
  PartnerInput,
  PartnerListParams,
  PartnerPage,
  PartnerRow,
} from "./api";

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));
let seq = 0;
const now = () => new Date().toISOString();

const partners: PartnerDetail[] = (
  [
    ["STALMET", "Stalmet Sp. z o.o.", "PL7251234567", "PL", "PLN", 10, "2", "ul. Hutnicza 12", "41-300", "Dąbrowa Górnicza"],
    ["FOAMTECH", "FoamTech Polska S.A.", "PL5269876543", "PL", "PLN", 7, "5", "ul. Przemysłowa 4", "95-100", "Zgierz"],
    ["TEXTILIA", "Textilia GmbH", "DE811234567", "DE", "EUR", 21, "3", "Webereistraße 8", "09111", "Chemnitz"],
    ["SCREWPRO", "ScrewPro Fasteners", "PL1132223344", "PL", "PLN", 3, "10", "ul. Śrubowa 1", "90-001", "Łódź"],
  ] as const
).map(([code, name, tax_id, country, currency, lead, tol, line1, postal, city]) => ({
  id: `bp-${code}`,
  code,
  name,
  tax_id,
  country,
  email: null,
  phone: null,
  currency,
  is_supplier: true,
  is_customer: false,
  supplier_lead_time_days: lead,
  supplier_receipt_tolerance_pct: tol,
  customer_payment_terms: null,
  note: null,
  is_active: true,
  updated_at: now(),
  addresses: [
    { id: `addr-${code}`, kind: "main", name: null, line1, line2: null, postal_code: postal, city, country, is_default: true },
  ],
}));

const duplicate = () => Object.assign(new Error("duplicate key"), { code: "23505" });

function toRow(p: PartnerDetail): PartnerRow {
  const { id, code, name, tax_id, country, currency, is_supplier, is_customer, is_active, supplier_lead_time_days } = p;
  return { id, code, name, tax_id, country, currency, is_supplier, is_customer, is_active, supplier_lead_time_days };
}

export const demoApi = {
  async list(p: PartnerListParams, cursor: string | null): Promise<PartnerPage> {
    await delay(60);
    const term = p.q.trim().toLowerCase();
    const rows = partners
      .filter((x) => (p.role === "all" ? true : p.role === "supplier" ? x.is_supplier : x.is_customer))
      .filter((x) => p.inactive || x.is_active)
      .filter((x) => !term || `${x.code} ${x.name} ${x.tax_id ?? ""}`.toLowerCase().includes(term))
      .filter((x) => !cursor || x.code > cursor)
      .sort((a, b) => (a.code < b.code ? -1 : 1))
      .map(toRow);
    return { rows: rows.slice(0, 50), nextCursor: rows.length > 50 ? rows[49]!.code : null };
  },
  async get(id: string): Promise<PartnerDetail | null> {
    await delay(40);
    const p = partners.find((x) => x.id === id);
    return p ? structuredClone(p) : null;
  },
  async create(input: PartnerInput): Promise<string> {
    await delay(80);
    if (partners.some((x) => x.code === input.code)) throw duplicate();
    const p: PartnerDetail = { ...input, id: `bp-${++seq}`, updated_at: now(), addresses: [] };
    partners.push(p);
    return p.id;
  },
  async update(id: string, input: Omit<PartnerInput, "code">): Promise<void> {
    await delay(80);
    const p = partners.find((x) => x.id === id);
    if (p) Object.assign(p, input, { updated_at: now() });
  },
  async addAddress(partnerId: string, input: AddressInput): Promise<void> {
    await delay(60);
    const p = partners.find((x) => x.id === partnerId);
    if (!p) return;
    if (input.is_default && p.addresses.some((a) => a.kind === input.kind && a.is_default)) throw duplicate();
    const address: PartnerAddress = { ...input, id: `addr-${++seq}` };
    p.addresses.push(address);
  },
  async deleteAddress(id: string): Promise<void> {
    await delay(60);
    for (const p of partners) p.addresses = p.addresses.filter((a) => a.id !== id);
  },
};
